import { Button, Drawer, Stack, TextField, Typography } from '@mui/material'
import type { ApprovalFlow } from '@ulysses-inc/harami_api_client'
import { AppRoleEnum } from '@ulysses-inc/harami_api_client'
import type { FormikHelpers } from 'formik'
import { useFormik } from 'formik'
import type React from 'react'
import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import Loading from 'src/components/loading/Loading'
import LoadingDrawer from 'src/components/loading/LoadingDrawer'
import Yup from 'src/features/validation/yup'
import {
  addApprovalFlow as addApprovalFlowAction,
  getApprovalFlow as getApprovalFlowAction,
  resetApprovalFlowForAdd as resetApprovalFlowForAddAction,
  resetApprovalFlowForUpdate as resetApprovalFlowForUpdateAction,
  updateApprovalFlow as updateApprovalFlowAction,
} from 'src/state/ducks/approvalFlows/actions'
import { getUsers as getUsersAction } from 'src/state/ducks/users/actions'
import { RootState } from 'src/state/store'
import EditApprovalFlowSteps from './EditApprovalFlowSteps'

interface OwnProps {
  approvalFlowId: string
  isShowDrawer: boolean
  isEditing: boolean
  onClose: () => void
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required().label('タイトル'),
})

const newValues = {
  name: '',
  description: '',
  steps: [{ approvers: [{}] }],
}

/**
 * 承認フロー編集ドロワーで取得するユーザー一覧の上限数。
 *
 * limit を指定しなかった場合、API 側のデフォルト値として 500 が設定される。
 * しかし、500 以上のユーザーが存在する場合はすべてのユーザーを取得できず、
 * 結果的に usersState に一部のデータしか存在しないことになる。
 *
 * 2,818 名登録の company で承認者選択ができないお客様事象 (HPB-6684) が発生したため、
 * 余裕を持って 5,000 を上限とする。
 *
 * ユーザー数の増加は契約変更を伴うため線形には増えず、3,000 名規模の登録があれば
 * 一括処理の依頼ベースで把握できる想定。現状最大の 2 倍弱を確保することで、
 * この上限を都度引き上げなくて済むようにしている。
 */
const APPROVERS_FETCH_LIMIT = 5000

const EditApprovalFlowDrawerContainer: React.FC<OwnProps> = ({
  isShowDrawer,
  isEditing,
  approvalFlowId,
  onClose,
}) => {
  const users = useSelector(
    ({
      usersState: {
        users: { users },
      },
    }: RootState) =>
      users.filter(({ role }) => role?.role !== AppRoleEnum.REPORTER),
    shallowEqual,
  )
  const approvalFlow = useSelector(
    (state: RootState) => state.approvalFlowsState.approvalFlow.approvalFlow,
    shallowEqual,
  )
  const isLoadingUsers = useSelector(
    (state: RootState) => state.usersState.users.isLoading,
    shallowEqual,
  )
  const isLoadingApprovalFlows = useSelector(
    (state: RootState) => state.approvalFlowsState.approvalFlow.isLoading,
    shallowEqual,
  )
  const {
    isLoading: isLoadingAddApprovalFlow,
    isCompleted: isCompletedAddApprovalFlow,
  } = useSelector(
    (state: RootState) => state.approvalFlowsState.addApprovalFlow,
    shallowEqual,
  )
  const {
    isLoading: isLoadingUpdateApprovalFlow,
    isCompleted: isCompletedUpdateApprovalFlow,
  } = useSelector(
    (state: RootState) => state.approvalFlowsState.updateApprovalFlow,
    shallowEqual,
  )

  const dispatch = useDispatch()

  const {
    values,
    handleChange,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: isEditing
      ? {
          name: approvalFlow.name,
          description: approvalFlow.description,
          steps: approvalFlow.steps,
        }
      : newValues,
    validationSchema,
    onSubmit: (
      values: ApprovalFlow,
      { setErrors }: FormikHelpers<ApprovalFlow>,
    ) => {
      // TODO move this into `validationSchema` to resolve the delay of displaying the error
      const isNotEmptyApprovers =
        (values.steps ?? [])
          .map(({ approvers }) => approvers ?? [])
          .reduce((prev, cur) => prev.concat(cur), [])
          .find(({ id }) => id === undefined) === undefined

      if (!isNotEmptyApprovers) {
        setErrors({ steps: '承認者は必須です' })
        return
      }

      // セットされている承認者がユーザー一覧に存在するかチェック
      // NOTE: 論理削除済みのユーザーは users に含まれない
      const allApproversAreActive = (values.steps ?? [])
        .map(({ approvers }) => approvers ?? [])
        .reduce((prev, cur) => prev.concat(cur), [])
        .every(({ id }) => users.some(user => user.id === id))

      // 設定されている承認者が存在しない（論理削除済み）の場合はエラーメッセージを表示
      if (!allApproversAreActive) {
        setErrors({ steps: '削除済みの承認者が設定されています' })
        return
      }

      if (isEditing) {
        dispatch(updateApprovalFlowAction(approvalFlowId, values))
      } else {
        dispatch(addApprovalFlowAction(values))
      }
    },
    enableReinitialize: true,
  })

  useEffect(() => {
    dispatch(getUsersAction({ limit: APPROVERS_FETCH_LIMIT }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEditing) {
      dispatch(getApprovalFlowAction(approvalFlowId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  // 新規作成の時に
  // - Formをリセット
  // - Drawerをクローズ
  // - isCompleted を false にリセットする
  useEffect(() => {
    if (!isEditing && isCompletedAddApprovalFlow) {
      setFieldValue('steps', [{ approvers: [{}] }])
      resetForm()
      onClose()
      dispatch(resetApprovalFlowForAddAction())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, isCompletedAddApprovalFlow])

  // 更新の時に
  // - Formをリセット
  // - Drawerをクローズ
  // - isCompleted を false にリセットする
  useEffect(() => {
    if (isEditing && isCompletedUpdateApprovalFlow) {
      setFieldValue('steps', [{ approvers: [{}] }])
      resetForm()
      onClose()
      dispatch(resetApprovalFlowForUpdateAction())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, isCompletedUpdateApprovalFlow])

  let isLoading = isLoadingApprovalFlows
  if (isEditing) {
    isLoading = isLoading || isLoadingUpdateApprovalFlow
  } else {
    isLoading = isLoading || isLoadingAddApprovalFlow
  }

  return (
    <Drawer
      anchor="right"
      open={isShowDrawer}
      onClose={() => {
        // ローディング中は閉じない
        if (isLoading) {
          return
        }
        onClose()
        resetForm()
      }}
      sx={{ '& .MuiDrawer-paper': { width: 600 } }}
    >
      <Stack spacing={2}>
        <Typography
          variant="h6"
          sx={{ py: 2, px: 3 }}
          data-testid="approval-flow-drawer-title"
        >
          承認フローの{isEditing ? '編集' : '追加'}
        </Typography>
        <Stack spacing={1} sx={{ px: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1 }}
            data-testid="approval-flow-name-label"
            htmlFor="approval-flow-name-input-field"
            component="label"
          >
            タイトル
          </Typography>
          <TextField
            fullWidth
            value={values.name}
            onChange={handleChange('name')}
            data-testid="approval-flow-name-input"
            id="approval-flow-name-input-field"
            slotProps={{
              htmlInput: {
                'data-testid': 'approval-flow-name-input-field',
              },
            }}
          />
          <Stack>
            <FormikErrorMessage name="name" errors={errors} touched={touched} />
          </Stack>
        </Stack>
        <Stack spacing={1} sx={{ px: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1 }}
            data-testid="approval-flow-description-label"
            component="label"
            htmlFor="approval-flow-description-input-field"
          >
            説明
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={values.description}
            onChange={handleChange('description')}
            data-testid="approval-flow-description-input"
            id="approval-flow-description-input-field"
            slotProps={{
              htmlInput: {
                'data-testid': 'approval-flow-description-input-field',
              },
            }}
          />
          <Stack>
            <FormikErrorMessage
              name="description"
              errors={errors}
              touched={touched}
            />
          </Stack>
        </Stack>
        <Stack spacing={1} sx={{ px: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 1 }}
            component="label"
            data-testid="approval-flow-steps-label"
          >
            承認フロー
          </Typography>
          {isLoadingUsers ? (
            <Loading />
          ) : (
            <EditApprovalFlowSteps
              steps={values.steps}
              users={users}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
            />
          )}
          <Stack>
            <FormikErrorMessage name="steps" errors={errors} />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          px={3}
          pb={2}
        >
          <Button
            disabled={isLoading}
            variant="outlined"
            onClick={() => {
              onClose()
              resetForm()
            }}
            data-testid="approval-flow-cancel-button"
          >
            キャンセル
          </Button>
          <LoadingDrawer
            spinning={isLoading}
            render={
              <Button
                variant="contained"
                onClick={() => {
                  handleSubmit()
                }}
                data-testid="approval-flow-save-button"
              >
                保存
              </Button>
            }
          />
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default EditApprovalFlowDrawerContainer
